import { Injectable } from '@angular/core';
import { DownloadService } from '../download/download.service';
import { BrowserSupportService } from 'src/app/core/browser-support.service';
import { SyncCookieService } from 'src/app/core/sync-cookie.service';
import { TransferItemService } from './transfer-item.service';
import { LoggerService } from 'src/app/core/logger.service';
import { BuildTransferItemService } from './build-transfer-item.service';
import { BroadcastService } from 'src/app/shared/services/broadcast.service';
import { FileItemService } from 'src/app/file/file-item.service';
import { BlendService } from 'src/app/shared/services/blend.service';
import { TransferQueueType, COMPAT_DOWNLOAD_THRESHOLD, TRANSFER_STATUS } from '../../models/transfer.model';
import { BlendEvent, CONSTANTS } from 'src/app/shared/models';
import { IPathData, ITransferItem, ITransferItemDownload, ITransferItemUpload } from 'src/sync';
import { FileItem } from 'src/app/shared/models/files.model';
import { UserService } from 'src/app/core/user.service';
import { StreamingSaveService } from 'src/app/core/storage/streaming-save.service';
import {
  StreamingSaveDestination,
  StreamingSaveDestinationOutcome,
  StreamingSaveOutcome,
  StreamingSaveSeam,
  StreamingSaveUnavailableReason,
} from 'src/app/shared/models/streaming-save.model';
import { sizeBucket } from 'src/app/shared/helpers/size-bucket';
import { StreamingSaveTelemetryService } from '../streaming-save-telemetry.service';
import { TransferType } from '../../models/transfer-type';

@Injectable({
  providedIn: 'root',
})
export class TransferService {
  public showTransferWindow = false;
  private transferStartedOn = 0;
  private bpsArr: number[] = [];

  constructor(
    private browserSupportService: BrowserSupportService,
    private syncCookieService: SyncCookieService,
    private transferItemService: TransferItemService,
    private downloadService: DownloadService,
    private loggerService: LoggerService,
    private buildTransferItemService: BuildTransferItemService,
    private broadcastService: BroadcastService,
    private fileItemService: FileItemService,
    private blendService: BlendService,
    private userService: UserService,
    private streamingSaveService: StreamingSaveService,
    private streamingSaveTelemetryService: StreamingSaveTelemetryService,
  ) {}

  public async queueUpload(files: FileList, isDragDrop: boolean, customCWD?: FileItem): Promise<any> {
    const ulQueue: Promise<ITransferItemUpload>[] = [];
    const fileExt = [];
    const mimeTypes = [];
    let fileSize = 0;
    this.loggerService.info('queueing an upload of ' + files.length);

    if (!this.browserSupportService.testFileUpload()) {
      this.loggerService.error('files were attempted to be uploaded but the browser does not support it');
      return;
    }

    for (let i = 0, len = files.length; i < len; i++) {
      const file = files[i];
      if (file) {
        if (file.name.length <= CONSTANTS.MAX_FILE_NAME_LEN) {
          ulQueue.push(this.buildTransferItemService.mkuploadItem(file, customCWD));
        } else {
          this.loggerService.warn(
            `Skipping file upload as length of filename exceeded ${CONSTANTS.MAX_FILE_NAME_LEN} characters`,
          );
          ulQueue.push(
            this.buildTransferItemService.mkuploadItem(file, customCWD).then((tItem) => {
              tItem.status = TRANSFER_STATUS.STATUS_ERR_NAME;
              return tItem;
            }),
          );
        }
        fileExt.push(this.fileItemService.getFileExt(file.name));
        const mimeType = this.fileItemService.getmimeType(this.fileItemService.getFileExt(file.name));
        if (mimeTypes.indexOf(mimeType) === -1) {
          mimeTypes.push(mimeType);
        }
        fileSize += file.size;
      }
    }

    const res = await Promise.all(ulQueue);
    await this.buildTransferItemService.addToQueue(res, TransferQueueType.UPLOAD);
    this.showTransfer();

    this.blendService.track(BlendEvent.UPLOAD_FILE, {
      isDragDrop: isDragDrop ? 'Yes' : 'No',
      mimeType: mimeTypes,
      fileSize: this.fileItemService.bytesToSize(fileSize),
      num_files: files.length,
    });
    return true;
  }

  public async queueDownload(files: FileItem[]): Promise<any> {
    // Must stay ahead of every await: the save picker needs the click's transient activation.
    const streamingDestination = this.requestStreamingDestination(files, false);
    const dlQueue: Promise<ITransferItemDownload>[] = [],
      mimeTypes = [];
    let totalSize = 0;
    const fileExt = [];

    const keyData = await this.buildTransferItemService.getFileKeys(files).catch((err) => this.handleKeyError(err));
    for (let i = 0, len = files.length; i < len; i++) {
      files[i] = { ...files[i], blobtype: 'btFILE' };
      dlQueue.push(this.buildTransferItemService.mkDownloadItem(files[i], keyData));
      const mimeType = files[i].mime_type;
      if (mimeTypes.indexOf(mimeType) === -1) {
        mimeTypes.push(mimeType);
      }
      totalSize = totalSize + files[i].size;
      fileExt.push(files[i].file_extension);
    }
    const result = await Promise.all(dlQueue).catch((err) => {
      this.loggerService.error('Error queing a file');
      throw new Error(err);
    });
    if (!(await this.attachStreamingHandle(streamingDestination, result, false))) {
      return false;
    }
    await this.buildTransferItemService.addToQueue(result, TransferQueueType.DOWNLOAD).catch((err) => {
      this.loggerService.error('Error queuing a file for download');
      throw new Error(err);
    });
    this.showTransfer();
    this.blendService.track(BlendEvent.DOWNLOAD_FILE, {
      isCompat: 'No',
      mimeTypes: mimeTypes,
      fileSize: this.fileItemService.bytesToSize(totalSize),
      sizeBucket: sizeBucket(totalSize),
      streamingUsed: !!result[0]?.streamingHandle,
    });
    return true;
  }

  public async queuePublicDownload(files: FileItem[]): Promise<any> {
    // Must stay ahead of every await: the save picker needs the click's transient activation.
    const streamingDestination = this.requestStreamingDestination(files, true);
    const dlQueue: Promise<ITransferItemDownload>[] = [];

    const keyData = await this.buildTransferItemService.getFileKeys(files).catch((err) => this.handleKeyError(err));
    for (let i = 0, len = files.length; i < len; i++) {
      files[i] = { ...files[i], blobtype: 'btFILE' };
      dlQueue.push(this.buildTransferItemService.mkDownloadItem(files[i], keyData));
    }
    const result = await Promise.all(dlQueue).catch((err) => {
      this.loggerService.error('Error queuePublicDownload a file');
      throw new Error(err);
    });
    if (!(await this.attachStreamingHandle(streamingDestination, result, true))) {
      return false;
    }
    await this.buildTransferItemService.addToQueue(result, TransferQueueType.DOWNLOAD).catch((err) => {
      this.loggerService.error('Error queuePublicDownload a file');
      throw new Error(err);
    });
    this.showTransfer();
    return true;
  }

  /** Null unless a single non-directory file qualifies; deliberately not awaited by the caller. */
  private requestStreamingDestination(files: FileItem[], isLink: boolean): Promise<StreamingSaveDestination> | null {
    // A multi-file or folder selection is not a streaming attempt, so it is not reported as a fallback.
    if (files.length !== 1 || files[0].is_dir) {
      return null;
    }
    const totalSizeBytes = files[0].size || 0;
    const capability = this.streamingSaveService.canStream(StreamingSaveSeam.SINGLE_FILE, totalSizeBytes, isLink);
    if (!capability.capable) {
      this.streamingSaveTelemetryService.track({
        seam: StreamingSaveSeam.SINGLE_FILE,
        outcome: StreamingSaveOutcome.FELL_BACK,
        fallbackReason: capability.reason,
        totalSizeBytes,
        isLink,
      });
      return null;
    }
    return this.streamingSaveService.requestDestination(files[0].name);
  }

  /** False means the user dismissed the picker, so nothing should be queued. */
  private async attachStreamingHandle(
    destination: Promise<StreamingSaveDestination> | null,
    tItems: ITransferItemDownload[],
    isLink: boolean,
  ): Promise<boolean> {
    if (!destination) {
      return true;
    }
    const resolved = await destination;
    const totalSizeBytes = tItems[0]?.filesize;
    if (resolved.outcome === StreamingSaveDestinationOutcome.CANCELLED) {
      this.streamingSaveTelemetryService.track({
        seam: StreamingSaveSeam.SINGLE_FILE,
        outcome: StreamingSaveOutcome.PICKER_CANCELLED,
        totalSizeBytes,
        isLink,
      });
      return false;
    }
    if (resolved.outcome === StreamingSaveDestinationOutcome.FAILED) {
      this.loggerService.warn('Streaming save unavailable, falling back to the staged download path');
      this.streamingSaveTelemetryService.track({
        seam: StreamingSaveSeam.SINGLE_FILE,
        outcome: StreamingSaveOutcome.FELL_BACK,
        fallbackReason: StreamingSaveUnavailableReason.PICKER_FAILED,
        totalSizeBytes,
        isLink,
      });
      return true;
    }
    if (tItems.length !== 1 || tItems[0].type === TransferType.PREVIEW) {
      await this.streamingSaveService.discardDestination(resolved.handle);
      return true;
    }
    tItems[0].streamingHandle = resolved.handle;
    tItems[0].filename = resolved.handle.name;
    this.streamingSaveTelemetryService.track({
      seam: StreamingSaveSeam.SINGLE_FILE,
      outcome: StreamingSaveOutcome.INITIATED,
      totalSizeBytes,
      isLink,
    });
    return true;
  }

  public async queuePublicUpload(files: FileList): Promise<any> {
    const ulQueue: Promise<ITransferItemUpload>[] = [];
    this.loggerService.info('queueing a public upload of ' + files.length);

    if (!this.browserSupportService.testFileUpload()) {
      this.loggerService.error('files were attempted to be uploaded but the browser does not support it');
      return new Error('files were attempted to be uploaded but the browser does not support it');
    }

    for (let i = 0, len = files.length; i < len; i++) {
      const file = files[i];
      if (file && file.size !== 0) {
        ulQueue.push(this.buildTransferItemService.mkPublicUploadItem(file));
      } else {
        this.loggerService.warn('skipping a 0 byte file');
      }
    }

    const res = await Promise.all(ulQueue).catch((err) => {
      this.loggerService.error('An error queing upload');
      throw Error(err);
    });
    await this.buildTransferItemService.addToQueue(res, TransferQueueType.UPLOAD).catch((err) => {
      this.loggerService.error('An error queuing upload');
      throw Error(err);
    });
    this.showTransfer();
    return true;
  }

  /**
   * Downloads a specific file, bypassing the queue.  Used for previews.
   * Since previews can occur in a compat link, it must also handle this
   * case where no decryption occurs and the 'renderFile' object is
   * filled out with the necessary URL
   * @param  {Object} pathitem  A pathitem object from a Path List
   * @return {Promise} Promise chain of downloading a file
   */
  public async getFile(file: FileItem, preview?: boolean): Promise<ITransferItemDownload> {
    if (!file.blobtype) {
      file.blobtype = 'btFILE';
    }
    if (file.compat && file.context == 'applink' && file.compaturl) {
      // Don't get file keys as link may not have been backfilled
      const tItem = await this.buildTransferItemService.mkDownloadItem(file, undefined);
      tItem.type = TransferType.PREVIEW;
      if (tItem.linkpasswordlock) {
        this.syncCookieService.setDownloadPubLink(tItem.linkpasswordlock);
      }
      this.buildTransferItemService.view.single = tItem;
      this.buildTransferItemService.view.single.renderFile = {
        dl_ready: true,
        type: 'url',
        url: file.compaturl,
        savemethod: 'dlattr',
      };
      return this.buildTransferItemService.view.single;
    } else {
      const keyData = await this.buildTransferItemService.getFileKeys([file]).catch((err) => {
        this.loggerService.error('Could not get file');
        throw err;
      });
      const tItem = await this.buildTransferItemService.mkDownloadItem(file, keyData).catch((err) => {
        this.loggerService.error('Could not get file');
        throw err;
      });
      tItem.type = TransferType.PREVIEW;
      this.buildTransferItemService.view.single = tItem;
      if (file.compat && !this.isImageHeic(file)) {
        const url = await this.buildTransferItemService
          .mkCompatUrl(tItem, this.buildTransferItemService.ACT_PREVIEW)
          .catch((err) => {
            throw err;
          });
        this.buildTransferItemService.view.single.renderFile = {
          dl_ready: true,
          type: 'url',
          url: url,
          savemethod: 'dlattr',
        };
        return this.buildTransferItemService.view.single;
      } else {
        let tItem;
        if (preview === true) {
          tItem = await this.downloadService.downloadPreview(this.buildTransferItemService.view.single).catch((err) => {
            throw err;
          });
        } else {
          tItem = await this.downloadService.downloadItem(this.buildTransferItemService.view.single).catch((err) => {
            throw err;
          });
        }
        return tItem;
      }
    }
  }
  public async getFileDocPreview(fileItem: FileItem): Promise<ITransferItemDownload> {
    const file = { ...fileItem };
    if (!file.blobtype) {
      file.blobtype = 'btFILE';
    }

    if (file.compat && file.context == 'applink' && file.compaturl) {
      // Don't get file keys as link may not have been backfilled
      let tItem = await this.buildTransferItemService.mkDownloadItem(file, undefined);
      tItem.type = TransferType.PREVIEW;
      if (tItem.linkpasswordlock) {
        this.syncCookieService.setDownloadPubLink(tItem.linkpasswordlock);
      }
      tItem.previewtoken = file.previewtoken;
      this.buildTransferItemService.view.single = tItem;
      this.buildTransferItemService.view.single.renderFile = {
        dl_ready: true,
        type: 'url',
        url: file.compaturl,
        savemethod: 'dlattr',
      };

      tItem = await this.downloadService.downloadCompat(this.buildTransferItemService.view.single).catch((err) => {
        throw err;
      });
      return tItem;
    } else {
      const keyData = await this.buildTransferItemService.getFileKeys([file]).catch((err) => {
        throw err;
      });
      let tItem = await this.buildTransferItemService.mkDownloadItem(file, keyData).catch((err) => {
        throw err;
      });
      tItem.type = TransferType.PREVIEW;
      this.buildTransferItemService.view.single = tItem;
      tItem = await this.downloadService.downloadPreview(this.buildTransferItemService.view.single).catch((err) => {
        throw err;
      });
      return tItem;
    }
  }

  public isImageHeic(item: FileItem): boolean {
    const name = item.name;
    const ext = name.substring(name.lastIndexOf('.') + 1).toLowerCase();
    let canPreview = false;
    switch (ext) {
      case 'heic':
      case 'heif':
        canPreview = item.has_thumb2 != 0;
        break;
      default:
        canPreview = false;
    }
    return canPreview && ext != name.toLowerCase();
  }

  /**
   * @ngdoc method
   * @name  getThumb
   * @methodOf sync.service:Transfer
   * @description
   * Gets a thumbnail for a given path item, typically used in previews.
   * The thumbnail is stored using thumb storage
   *
   * @param  {Object} pathitem [description]
   * @param  {Array.Object} keyData  [description]
   * @param  {Array|ArrayBuffer} bytes    [description]
   * @return {Promise}          [description]
   */
  public async getThumb(file: FileItem, keyData: IPathData, bytes: ArrayBuffer): Promise<ITransferItem> {
    file.blobtype = file.thumbData.blobtype;
    const tItem = await this.buildTransferItemService.mkDownloadItem(file, keyData);
    return await this.downloadService.saveThumb(tItem, bytes);
  }

  public reset(): void {
    const { download, upload } = this.buildTransferItemService.view;
    [download, upload].forEach((view) => {
      view.queue = [];
      view.queueIdx = 0;
      view.isProcessing = false;
    });
  }

  public showTransfer(): void {
    this.showTransferWindow = true;
  }

  public closeTransfer(): void {
    this.showTransferWindow = false;
    this.transferStartedOn = 0;
    this.bpsArr = [];
  }

  /**
   * Strips upload + download queue entries matching `fileName` so callers
   * that perform internal transfers (e.g. text-editor saves) don't leave
   * lingering entries in the transfer panel after they complete.
   */
  public removeFromQueueByName(fileName: string): void {
    const matches = (item: { filename?: string; name?: string } | null | undefined): boolean =>
      !!item && (item.filename === fileName || item.name === fileName);
    const view = this.buildTransferItemService.view;
    if (Array.isArray(view?.upload?.queue)) {
      view.upload.queue = view.upload.queue.filter((item) => !matches(item));
    }
    if (Array.isArray(view?.download?.queue)) {
      view.download.queue = view.download.queue.filter((item) => !matches(item));
    }
  }

  public hasActiveTransfers(): boolean {
    const view = this.buildTransferItemService.view;
    return !!(view?.upload?.isProcessing || view?.download?.isProcessing);
  }

  private handleKeyError(err: any): Error {
    this.loggerService.error('Error retrieving keys');
    this.loggerService.error(err);
    try {
      const data = JSON.stringify(err);
      this.loggerService.error(data);
    } catch (e) {
      this.loggerService.error(e.toString());
    }
    return new Error(err);
  }

  /**
   * queue upload for blank document
   * @param file
   * @returns
   */
  public async queuePublicBlankDocUpload(file: File, customCWD?: FileItem): Promise<any> {
    const ulQueue: Promise<ITransferItemUpload>[] = [];
    this.loggerService.info('queueing a public upload of ' + file.size);

    if (!this.browserSupportService.testFileUpload()) {
      this.loggerService.error('files were attempted to be uploaded but the browser does not support it');
      return;
    }

    if (file && file.size !== 0) {
      ulQueue.push(this.buildTransferItemService.mkuploadItem(file, customCWD));
    } else {
      this.loggerService.warn('skipping a 0 byte file');
    }

    const res = await Promise.all(ulQueue).catch((err) => {
      this.loggerService.error('An error queing upload');
      throw err;
    });
    await this.buildTransferItemService.addToQueue(res, TransferQueueType.UPLOAD);
    return true;
  }

  public async singleFileDownload(fileItem: FileItem): Promise<void> {
    if (this.userService.isDownloadAcceleratorAllowed() && fileItem.size >= COMPAT_DOWNLOAD_THRESHOLD) {
      const url = await this.buildTransferItemService.getCompatUrl(
        fileItem,
        this.buildTransferItemService.ACT_DOWNLOAD,
      );
      window.location.href = url;
    } else {
      this.queueDownload([fileItem]);
    }
  }
}
